import React from 'react';
import { Plus, Trash2, MapPin } from 'lucide-react';

type TransportMode = 'TRAIN' | 'BUS' | 'TAXI' | 'AIR';

export interface HowToReachStation {
  name: string;
  options: Array<{ mode: TransportMode; details: string }>;
}

interface HowToReachFormProps {
  stations: HowToReachStation[];
  onChange: (stations: HowToReachStation[]) => void;
  inputClass?: string;
  /** Non-editable last section shows "Arrived" and this school name */
  schoolName?: string;
}

const HowToReachForm: React.FC<HowToReachFormProps> = ({
  stations,
  onChange,
  inputClass = 'w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white',
  schoolName,
}) => {
  const updateStation = (idx: number, updater: (s: HowToReachStation) => HowToReachStation) => {
    onChange(stations.map((s, i) => (i === idx ? updater(s) : s)));
  };

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header - compact on mobile */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-4 py-4 sm:px-6 sm:py-5 border-b border-primary-200">
        <div className="flex items-start gap-3 sm:items-center">
          <div className="flex-shrink-0 p-2 sm:p-2.5 bg-primary-600/10 rounded-lg sm:rounded-xl">
            <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 text-base sm:text-lg">How to Reach</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5 leading-snug">
              Add transit stations in order. Transport options show how to reach the school from each stop.
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {/* Vertical timeline */}
        <div className="relative">
          {/* Vertical dotted line - position matches step circle center on mobile (left-5) and desktop (left-6) */}
          <div
            className="absolute left-5 sm:left-6 top-0 bottom-0 w-0.5"
            style={{
              background: 'repeating-linear-gradient(to bottom, rgb(255 107 53 / 0.4) 0px, rgb(255 107 53 / 0.4) 8px, transparent 8px, transparent 16px)',
            }}
          />

          <div className="space-y-6 sm:space-y-8">
            {stations.map((station, stationIdx) => (
              <div key={stationIdx} className="relative flex gap-3 sm:gap-5">
                {/* Step circle - smaller on mobile */}
                <div className="relative z-10 flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white font-bold text-sm sm:text-base flex items-center justify-center shadow-lg ring-2 sm:ring-4 ring-white">
                  {stationIdx + 1}
                </div>

                {/* Station card */}
                <div className="flex-1 min-w-0 bg-gray-50/80 rounded-xl border border-gray-200 overflow-hidden hover:border-primary-200 transition-colors">
                  <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                    {/* Station name row - stacks on mobile so input is full width */}
                    <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 sm:items-start">
                      <div className="flex-1 min-w-0 w-full">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 sm:mb-1.5">
                          Station or arrival point
                        </label>
                        <input
                          type="text"
                          value={station.name}
                          onChange={(e) => updateStation(stationIdx, (s) => ({ ...s, name: e.target.value }))}
                          placeholder="e.g. Rishikesh Railway Station"
                          className={inputClass}
                        />
                      </div>
                      {stations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => onChange(stations.filter((_, i) => i !== stationIdx))}
                          className="self-end sm:self-auto sm:mt-7 p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all touch-manipulation"
                          aria-label="Remove station"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div>
                      <div className="space-y-2">
                        {station.options.map((opt, optIdx) => (
                            <div
                              key={optIdx}
                              className="flex flex-col gap-2 sm:flex-row sm:gap-2 sm:items-center p-2.5 sm:p-2 rounded-lg bg-white border border-gray-200"
                            >
                              <select
                                value={opt.mode}
                                onChange={(e) =>
                                  updateStation(stationIdx, (s) => ({
                                    ...s,
                                    options: s.options.map((o, oi) =>
                                      oi === optIdx ? { ...o, mode: e.target.value as TransportMode } : o
                                    ),
                                  }))
                                }
                                className="w-full sm:w-[120px] flex-shrink-0 px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-sm font-medium"
                              >
                                <option value="TRAIN">Train</option>
                                <option value="BUS">Bus</option>
                                <option value="TAXI">Taxi</option>
                                <option value="AIR">Air</option>
                              </select>
                              <div className="flex gap-2 flex-1 min-w-0 w-full sm:w-auto">
                                <input
                                  type="text"
                                  value={opt.details}
                                  onChange={(e) =>
                                    updateStation(stationIdx, (s) => ({
                                      ...s,
                                      options: s.options.map((o, oi) =>
                                        oi === optIdx ? { ...o, details: e.target.value } : o
                                      ),
                                    }))
                                  }
                                  placeholder="Details (e.g. duration, frequency)"
                                  className="flex-1 min-w-0 px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm placeholder:text-gray-400"
                                />
                                {station.options.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateStation(stationIdx, (s) => ({
                                        ...s,
                                        options: s.options.filter((_, oi) => oi !== optIdx),
                                      }))
                                    }
                                    className="flex-shrink-0 p-2.5 sm:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors touch-manipulation"
                                    aria-label="Remove option"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        {station.options.length < 10 && (
                          <button
                            type="button"
                            onClick={() =>
                              updateStation(stationIdx, (s) => ({
                                ...s,
                                options: [...s.options, { mode: 'BUS' as const, details: '' }],
                              }))
                            }
                            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium py-2 sm:py-1.5 touch-manipulation"
                          >
                            <Plus className="h-4 w-4 flex-shrink-0" /> Add transport option
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add another station - between stations and arrived */}
            {stations.length < 20 && (
              <div className="relative flex gap-3 sm:gap-5">
                <div className="relative z-10 flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-dashed border-primary-300 flex items-center justify-center bg-white">
                  <Plus className="h-4 w-4 sm:h-5 sm:w-5 text-primary-500" />
                </div>
                <div className="flex-1 min-w-0 flex items-center">
                  <button
                    type="button"
                    onClick={() => onChange([...stations, { name: '', options: [{ mode: 'TRAIN', details: '' }] }])}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-xl transition-colors touch-manipulation"
                  >
                    <Plus className="h-4 w-4 flex-shrink-0" /> {stations.length === 0 ? 'Add first station' : 'Add another station'}
                  </button>
                </div>
              </div>
            )}

            {/* Arrived section - non-editable */}
            <div className="relative flex gap-3 sm:gap-5">
              <div className="relative z-10 flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-100 border-2 border-dashed border-primary-400 flex items-center justify-center">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0 rounded-xl border-2 border-primary-200 bg-gradient-to-br from-primary-50 to-white p-3 sm:p-4">
                <p className="text-xs font-medium text-primary-600 uppercase tracking-wider mb-0.5 sm:mb-1">Arrived</p>
                <p className="font-semibold text-gray-900 text-base sm:text-lg break-words">
                  {schoolName?.trim() || '—'}
                </p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Final destination</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToReachForm;
